import React from 'react';
import { AccountCircle, Business, AccountBox } from '@material-ui/icons';
import { useSelectDebounce } from 'utils/react';
import { findMainOrganizationRole } from 'utils/user';
import Fields from 'ui/form/FieldsForm';
import PaperForm from 'ui/form/PaperForm';
import ButtonsForm from 'ui/form/ButtonsForm';
import SectionForm from 'ui/form/SectionForm';
import AsyncSelect from 'react-select/async';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import SearchIcon from '@material-ui/icons/Search';

import TeamPart from '../parts/TeamPart';
import { isCustomerType, ORGANIZATION_TYPES } from '../consts';
import { naturalSort } from 'utils/sorting';

const getAccountTypesOptions = (user, types = ORGANIZATION_TYPES) => {
    const role = findMainOrganizationRole(user);
    return types[role] || {};
};

const useStyles = makeStyles(() => ({
    searchIcon: {
        width: 36,
        color: 'hsl(0,0%,80%)',
        '&:hover': {
            color: 'hsl(0,0%,60%)',
        },
    },
    searchIconActive: {
        width: 36,
        color: 'hsl(0,0%,40%)',
    },
}));

const toOptionsArr = options =>
    Object.keys(options).map(key => ({
        label: options[key],
        value: key,
    }));

export default function CompanyIntegrationDetailsStep({
    onChange,
    goNext,
    goBack,
    errors,
    values,
    extra,
}) {
    const { config, service, lookups } = values;
    const onChangeType = type => {
        if (type !== values.type) {
            onChange({
                createPartnerAccount: !isCustomerType(type),
                type,
            });
        }
    };

    const onChangeName = value => {
        onChange({
            business_name: value.label,
            name: value.label,
        });
    };
    const accountTypesOptions = getAccountTypesOptions(extra.user);
    if (!values.type) {
        onChangeType(Object.keys(accountTypesOptions)[0]);
    }

    const loadOptions = async value => {
        if (!value) {
            return [];
        }
        let result = await config.serviceProvider.companiesExternal(
            service,
            value
        );
        result = toOptionsArr(result);
        onChange({ externalCompanies: result });
        result = result.filter(item => {
            const values = Object.keys(lookups.external_company) || [];
            return !values.includes(item.value);
        });

        return naturalSort(result, item => item.label);
    };

    const { setSearchTerm, setCallback } = useSelectDebounce(loadOptions);
    const handleLoadOptions = (term, callback) => {
        setCallback(() => {
            return callback;
        });
        setSearchTerm(term);
    };

    const getCompanyByName = name =>
        values.externalCompanies.find(item => item.label === name);

    return (
        <PaperForm title="Company Details">
            <SectionForm icon={AccountCircle}>
                <Fields.Select
                    label="Account type"
                    value={values.type}
                    error={errors.type}
                    disabled={!extra.creating}
                    onChange={onChangeType}
                    options={accountTypesOptions}
                />
            </SectionForm>
            <SectionForm icon={Business}>
                <AsyncSelect
                    value={getCompanyByName(values.business_name) || ''}
                    onChange={onChangeName}
                    placeholder={`Select company from ${config.label}`}
                    cacheOptions
                    loadOptions={loadOptions && handleLoadOptions}
                    isSearchable
                    styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPosition="absolute"
                    noOptionsMessage={() => 'Start typing to search'}
                    menuPortalTarget={document.body}
                    components={{
                        DropdownIndicator,
                    }}
                />
            </SectionForm>
            <SectionForm icon={AccountBox}>
                <TeamPart values={values} errors={errors} onChange={onChange} />
            </SectionForm>
            <ButtonsForm>
                <ButtonsForm.BackButton
                    label="Cancel"
                    onClick={goBack}
                    loading={values.loading}
                />
                <ButtonsForm.NextButton
                    onClick={goNext}
                    loading={values.loading}
                />
            </ButtonsForm>
        </PaperForm>
    );
}

const DropdownIndicator = ({ isFocused }) => {
    const classes = useStyles();
    const className = classNames({
        [classes.searchIcon]: !isFocused,
        [classes.searchIconActive]: isFocused,
    });
    return <SearchIcon className={className} />;
};
